import { Injectable, signal } from '@angular/core';

export const THEME_STORAGE_KEY = 'bpmn-color-scheme';

export type AppTheme = 'ink' | 'olive' | 'amber' | 'violet';

export type AppThemeOption = {
  id: AppTheme;
  label: string;
  swatch: string;
};

export const THEME_OPTIONS: readonly AppThemeOption[] = [
  { id: 'ink', label: 'Нейтральна', swatch: '#3b3b3b' },
  { id: 'olive', label: 'Оливкова', swatch: '#5c641e' },
  { id: 'amber', label: 'Бурштинова', swatch: '#795a0c' },
  { id: 'violet', label: 'Фіолетова', swatch: '#5b5992' },
];

export function isAppTheme(value: string | null | undefined): value is AppTheme {
  return value === 'ink' || value === 'olive' || value === 'amber' || value === 'violet';
}

export function readStoredTheme(): AppTheme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (isAppTheme(stored)) return stored;
  } catch {
    // localStorage can be unavailable in private contexts.
  }
  return 'ink';
}

export function applyTheme(theme: AppTheme): void {
  document.documentElement.dataset['theme'] = theme;
}

/** Apply before bootstrap so the first paint uses the saved scheme. */
export function applyStoredTheme(): void {
  applyTheme(readStoredTheme());
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly options = THEME_OPTIONS;
  readonly theme = signal<AppTheme>(readStoredTheme());

  constructor() {
    applyTheme(this.theme());
  }

  setTheme(theme: AppTheme): void {
    this.theme.set(theme);
    applyTheme(theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Ignore storage failures; the in-memory theme still applies.
    }
  }
}
